/**
 * Integrytas Group - Common Security Module (CSM)
 * Page: CSM.I18N.MANAGER
 *
 * One screen showing ALL translatable text — screen prompts (the i18next dot-path
 * bundle, overridable in csm_i18n_string) AND data content (the Translation-Mapping
 * _tl rows, surfaced via csm_i18n_data_strings) — for every active locale at once.
 * The user's home language is the first editable column. Edit inline; the find box
 * is the primary navigation; find/replace and two AI actions — translate only the
 * missing strings, or wipe the Target locale and re-translate every string from the
 * Source locale — operate over whatever is currently filtered.
 *
 * Two save paths:
 *  - Prompt rows  -> upsert into csm_i18n_string (an override on the static bundle).
 *  - Data rows    -> csm_i18n_set_data_string RPC (writes the relevant _tl row via
 *                    the Common Routine, exactly as the owning module would).
 */

import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import type { PageComponentProps } from '../../pages/page_components';
import { apiDb } from '../../lib/api_client';
import { useActiveLocale } from '../../lib/use_active_locale';
import { useLanguageOptions } from '../../lib/use_language_options';
import { hasAccess, getForensicActorId } from '../../types/auth';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, GRID_TABLE_CLASS } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { PROMPT_KEYS, promptDefault } from '../../lib/i18n_flatten';
import { aiClient } from '../../lib/ai/ai_client';
import { FilteredModalSingleSelect } from '../../components/common/FilteredModalSingleSelect';
import { EmptyState } from '../../components/common/EmptyState';
import { ErrorBanner } from '../../components/common/ErrorBanner';
import { ConfirmModal } from '../../components/common/ConfirmModal';
import { PageHeader } from '../../components/common/PageHeader';
import { ContentCard } from '../../components/common/ContentCard';
import { Search, RefreshCw } from 'lucide-react';

const PAGE_CODE = 'CSM.I18N.MANAGER';

// Cap the number of rows actually painted so a blank find box (≈1500 prompt rows
// plus data rows) never stalls the grid. The find box is the real navigation.
const RENDER_CAP = 300;

type RowKind = 'prompt' | 'data';

interface Row {
  id: string;
  kind: RowKind;
  label: string;
  prompt_key?: string;
  table_name?: string;
  row_key?: Record<string, unknown>;
  values: Record<string, string>;
}

interface DataStringRow {
  table_name: string;
  row_key: Record<string, unknown>;
  locale_code: string;
  display_text: string;
}

export function I18nManagerPage({ session }: PageComponentProps) {
  return (
    <PersonalizationProvider pageCode={PAGE_CODE} session={session}>
      <I18nManagerPageInner session={session} />
    </PersonalizationProvider>
  );
}

function I18nManagerPageInner({ session }: PageComponentProps) {
  // §6/§17: the write below is page-scoped, so the affordance follows
  // csm_role_page_access. The server refuses it regardless; this stops the
  // page offering an action that can only error.
  const canChange = hasAccess(session, PAGE_CODE, 'change');
  const { t } = useTranslation();
  const actor = getForensicActorId(session);
  // §8: the working/home locale follows the ACTIVE UI locale (no stale-preference exception).
  const homeLocale = useActiveLocale();
  const { options: languageOptions } = useLanguageOptions();
  const localeLabel = useCallback((code: string) => languageOptions.find(o => o.code === code)?.name ?? code, [languageOptions]);

  const [locales, setLocales] = useState<string[]>([]);
  const [rows, setRows] = useState<Row[]>([]);
  // Local edits, keyed `${rowId} ${locale}`, so typing stays smooth and survives
  // until blur saves it back.
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // §17.B13 related-children disclosure. Each row is a single translatable string
  // with no child collection, so the control renders inert until relations wired.
  const detail = useExpandedRows<string>();
  const buildRelatedGroups = (_id: string): RelatedChildGroup[] => []; // TODO: wire relations
  const hasRelated = (id: string) => buildRelatedGroups(id).some(g => g.items.length > 0);

  // Toolbar state.
  const [filter, setFilter] = useState('');
  // §17.B5: facet on the one logical column of this list — the string kind
  // (screen prompt vs. data-content _tl row).
  const [kindFacet, setKindFacet] = useState<string[]>([]);
  const [replaceWith, setReplaceWith] = useState('');
  // The Source locale drives BOTH the AI actions (translate from) and the
  // find/replace column. Defaults to the home/UI locale.
  const [replaceLocale, setReplaceLocale] = useState(homeLocale);
  const [aiLocale, setAiLocale] = useState('');
  // Which AI action is awaiting confirmation: 'missing' fills only untranslated
  // Target cells; 'all' overwrites every Target cell, re-translating from Source.
  const [confirmAction, setConfirmAction] = useState<null | 'missing' | 'all'>(null);

  const reload = async () => {
    setBusy(true); setError(null);
    try {
      // 1. Active locales, home language first.
      const { data: locData, error: lErr } = await apiDb
        .from('csm_language_master')
        .select('internal_code')
        .eq('is_active', 'Y')
        .order('internal_code');
      if (lErr) throw lErr;
      const all = ((locData ?? []) as { internal_code: string }[]).map(l => l.internal_code);
      const ordered = [
        ...(all.includes(homeLocale) ? [homeLocale] : []),
        ...all.filter(l => l !== homeLocale),
      ];
      setLocales(ordered);

      // 2. Prompt overrides -> `${locale} ${key}` -> text.
      const { data: ovData, error: oErr } = await apiDb
        .from('csm_i18n_string')
        .select('locale_code, string_key, display_text');
      if (oErr) throw oErr;
      const overrides = new Map<string, string>();
      for (const o of (ovData ?? []) as { locale_code: string; string_key: string; display_text: string }[]) {
        overrides.set(`${o.locale_code} ${o.string_key}`, o.display_text);
      }

      // 3. Data translations -> grouped per (table_name + JSON.stringify(row_key)).
      const { data: dataStrings, error: dErr } = await apiDb.rpc<DataStringRow[]>('csm_i18n_data_strings');
      if (dErr) throw dErr;

      // --- Build prompt rows: one per known dot-path key.
      const promptRows: Row[] = PROMPT_KEYS.map(key => {
        const values: Record<string, string> = {};
        for (const loc of ordered) {
          values[loc] = overrides.get(`${loc} ${key}`) ?? promptDefault(key, loc);
        }
        return { id: `prompt ${key}`, kind: 'prompt', label: key, prompt_key: key, values };
      });

      // --- Build data rows: one logical row per (table, row_key).
      const groups = new Map<string, { table_name: string; row_key: Record<string, unknown>; values: Record<string, string> }>();
      for (const d of (dataStrings ?? []) as DataStringRow[]) {
        const gid = `${d.table_name}|${JSON.stringify(d.row_key)}`;
        let g = groups.get(gid);
        if (!g) { g = { table_name: d.table_name, row_key: d.row_key, values: {} }; groups.set(gid, g); }
        g.values[d.locale_code] = d.display_text ?? '';
      }
      const dataRows: Row[] = Array.from(groups.entries()).map(([gid, g]) => {
        const values: Record<string, string> = {};
        for (const loc of ordered) values[loc] = g.values[loc] ?? '';
        const label = `${g.table_name}: ${Object.values(g.row_key).join(' / ')}`;
        return { id: `data ${gid}`, kind: 'data', label, table_name: g.table_name, row_key: g.row_key, values };
      });
      dataRows.sort((a, b) => a.label.localeCompare(b.label));

      setRows([...promptRows, ...dataRows]);
      setEdits({});
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // reload is re-created each render; run once on mount.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void reload(); }, []);

  // Current value for a cell = pending edit if present, else the loaded value.
  const cellValue = (r: Row, locale: string): string => {
    const k = `${r.id} ${locale}`;
    return k in edits ? edits[k] : (r.values[locale] ?? '');
  };

  const kindOptions = useMemo(() => [
    { value: 'prompt', label: t('i18n_manager.kind.prompt', 'Screen prompts') },
    { value: 'data',   label: t('i18n_manager.kind.data', 'Data content') },
  ], [t]);
  const anyFilterActive = kindFacet.length > 0 || filter.trim().length > 0;
  const clearAll = () => { setFilter(''); setKindFacet([]); };

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    // A row passes the facet when no values are selected, or its kind matches one.
    const matchesFacets = (r: Row) => kindFacet.length === 0 || kindFacet.includes(r.kind);
    return rows.filter(r =>
      matchesFacets(r) &&
      (!q ||
        r.label.toLowerCase().includes(q) ||
        Object.values(r.values).some(v => v.toLowerCase().includes(q))),
    );
  }, [rows, filter, kindFacet]);

  const overflow = Math.max(0, filtered.length - RENDER_CAP);
  const visibleRows = overflow > 0 ? filtered.slice(0, RENDER_CAP) : filtered;

  // Persist one cell. Prompt vs data write to entirely different places.
  const saveCell = async (r: Row, locale: string, text: string) => {
    try {
      if (r.kind === 'prompt') {
        const { error: e } = await apiDb.from('csm_i18n_string').upsert(
          { locale_code: locale, string_key: r.prompt_key, display_text: text, modified_by: actor },
          { onConflict: 'locale_code,string_key' },
        );
        if (e) throw e;
      } else {
        const { error: e } = await apiDb.rpc('csm_i18n_set_data_string', {
          p_table: r.table_name, p_key: r.row_key, p_locale: locale, p_text: text,
        });
        if (e) throw e;
      }
      // Reflect the saved value back into the row model and drop the pending edit.
      setRows(prev => prev.map(x => x.id === r.id ? { ...x, values: { ...x.values, [locale]: text } } : x));
      setEdits(prev => Object.fromEntries(Object.entries(prev).filter(([k]) => k !== `${r.id} ${locale}`)));
    } catch (e) {
      setError((e as Error).message);
    }
  };

  // Save on blur only if the value actually changed.
  const onCellBlur = (r: Row, locale: string) => {
    const k = `${r.id} ${locale}`;
    if (!(k in edits)) return;
    const next = edits[k];
    if (next === (r.values[locale] ?? '')) {
      setEdits(prev => Object.fromEntries(Object.entries(prev).filter(([kk]) => kk !== k)));
      return;
    }
    void saveCell(r, locale, next);
  };

  // Find/Replace over the CURRENTLY-FILTERED rows, in the chosen locale.
  const runReplaceAll = async () => {
    const find = filter.trim();
    if (!find) return; // guard: never replace on an empty find
    setBusy(true); setError(null);
    try {
      for (const r of filtered) {
        const current = cellValue(r, replaceLocale);
        if (!current.includes(find)) continue;
        const next = current.split(find).join(replaceWith);
        if (next === current) continue;
        await saveCell(r, replaceLocale, next);
      }
    } finally {
      setBusy(false);
    }
  };

  // Rows whose SOURCE cell actually has text to translate FROM. Empty-source rows
  // are skipped (nothing to translate, and the label is a key path, not content).
  const translatable = useMemo(
    // cellValue reads `edits` too, so recompute as pending edits change.
    () => filtered.filter(r => cellValue(r, replaceLocale).trim() !== ''),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filtered, replaceLocale, edits],
  );
  // Of those, the ones still MISSING a Target translation — the target cell is
  // empty, or still identical to the source text (i.e. never actually translated).
  const missing = useMemo(
    () => translatable.filter(r => {
      const v = cellValue(r, aiLocale);
      return v === '' || v === cellValue(r, replaceLocale);
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [translatable, aiLocale, replaceLocale, edits],
  );

  // Translate a set of rows from the Source locale into the Target locale,
  // overwriting each target cell with the AI result.
  const translateRows = async (targetRows: Row[]) => {
    const target = aiLocale;
    if (!target || target === replaceLocale || targetRows.length === 0) return;
    setBusy(true); setError(null);
    try {
      const texts = targetRows.map(r => cellValue(r, replaceLocale));
      const { data, error: e } = await aiClient.translate(texts, target);
      if (e) { setError(e.message); return; }
      const translations = data?.translations ?? [];
      for (let i = 0; i < targetRows.length; i++) {
        const out = translations[i];
        if (typeof out !== 'string' || out === '') continue;
        await saveCell(targetRows[i], target, out);
      }
    } finally {
      setBusy(false);
    }
  };

  // Run whichever AI action the user just confirmed, then dismiss the dialog.
  const runConfirmedAction = () => {
    const rowsToDo = confirmAction === 'all' ? translatable : missing;
    setConfirmAction(null);
    void translateRows(rowsToDo);
  };

  const cols: GridColumnDef[] = useMemo(() => [
    { key: 'label', header: 'String', defaultWidth: 320 },
    ...locales.map(l => ({ key: l, header: localeLabel(l), defaultWidth: 220 })),
  ], [locales, localeLabel]);

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-auto p-6">
        <PageHeader
          title={t('i18n_manager.title', 'Translations')}
          description={t('i18n_manager.list.description', 'Every translatable string — screen prompts and data content — across all active locales, editable inline.')}
          actions={
            <button type="button" onClick={() => void reload()} className="btn-secondary flex items-center gap-1.5">
              <RefreshCw className="h-3.5 w-3.5" /> {t('i18n_manager.refresh', 'Refresh')}
            </button>
          }
        />
        {error && (
          <ErrorBanner
            message={t('i18n_manager.error_banner', "Couldn't complete the last translations operation.")}
            detail={error}
            retry_label={t('common.retry', 'Retry')}
            on_retry={() => void reload()}
            details_label={t('common.details', 'Details')}
          />
        )}

        <ContentCard className="flex min-h-0 flex-1 flex-col">
          {/* Toolbar */}
          <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-csm-line bg-white px-3 py-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-csm-ink4" aria-hidden />
              <input
                type="text"
                value={filter}
                onChange={e => setFilter(e.target.value)}
                placeholder={t('i18n_manager.find_placeholder', 'Find (label or any text)…')}
                className="h-8 w-72 rounded-csm-control border border-csm-border pl-8 pr-2 text-csm-md focus:border-primary focus:outline-none"
              />
            </div>
            <FacetFilter label={t('i18n_manager.col.kind', 'Kind')} options={kindOptions} selected={kindFacet} onChange={setKindFacet} />
            <ClearFiltersButton active={anyFilterActive} onClear={clearAll} label={t('common.clear_filters', 'Clear filters')} />

            {/* Find / Replace — reuses the Find box above as the search term. */}
            <span className="ml-2 h-5 border-l border-csm-border" />
            <input
              type="text"
              value={replaceWith}
              onChange={e => setReplaceWith(e.target.value)}
              placeholder={t('i18n_manager.replace_placeholder', 'Replace with…')}
              className="h-8 w-48 rounded-csm-control border border-csm-border px-2 text-csm-md focus:border-primary focus:outline-none"
            />
            <div className="w-40">
              <FilteredModalSingleSelect
                label={t('i18n_manager.source_locale', 'Source locale')}
                options={locales.map(l => ({ internal_code: l, display_text: localeLabel(l) }))}
                selectedCode={replaceLocale || null}
                onChange={(code) => setReplaceLocale(code ?? homeLocale)}
                allowClear={false}
              />
            </div>
            {canChange && (<button
              onClick={() => void runReplaceAll()}
              disabled={busy || !filter.trim()}
              title={!filter.trim() ? t('i18n_manager.replace_title_disabled', 'Type a Find term first') : t('i18n_manager.replace_title', 'Replace in the filtered rows')}
              className="h-8 rounded-csm-control border border-csm-border bg-white px-2 text-csm-sm hover:bg-csm-surface-alt disabled:opacity-50"
            >
              {t('i18n_manager.replace_all', 'Replace all')}
            </button>)}

            {/* AI-translate — Source locale (above) -> Target locale (any other). */}
            <span className="ml-2 h-5 border-l border-csm-border" />
            <div className="w-40">
              <FilteredModalSingleSelect
                label={t('i18n_manager.target_locale', 'Target locale…')}
                options={locales.filter(l => l !== replaceLocale).map(l => ({ internal_code: l, display_text: localeLabel(l) }))}
                selectedCode={aiLocale || null}
                onChange={(code) => setAiLocale(code ?? '')}
                placeholder={t('i18n_manager.target_locale', 'Target locale…')}
              />
            </div>
            {canChange && (<button
              onClick={() => setConfirmAction('missing')}
              disabled={busy || !aiLocale || aiLocale === replaceLocale || missing.length === 0}
              title={t('i18n_manager.translate_missing_title', 'Translate only the strings with no {{target}} translation yet', { target: localeLabel(aiLocale) })}
              className="h-8 rounded-csm-control border border-primary bg-white px-2 text-csm-sm text-primary hover:bg-csm-surface-alt disabled:opacity-50"
            >
              {t('i18n_manager.translate_missing', 'Translate missing')}
            </button>)}
            {canChange && (<button
              onClick={() => setConfirmAction('all')}
              disabled={busy || !aiLocale || aiLocale === replaceLocale || translatable.length === 0}
              title={t('i18n_manager.retranslate_all_title', 'Overwrite every {{target}} translation, re-translating from {{source}}', { target: localeLabel(aiLocale), source: localeLabel(replaceLocale) })}
              className="h-8 rounded-csm-control border border-primary bg-primary px-2 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-50"
            >
              {t('i18n_manager.retranslate_all', 'Re-translate all')}
            </button>)}

            <div className="ml-auto flex items-center gap-2">
              {busy && <span className="text-csm-xs text-csm-watermark">{t('i18n_manager.working', 'Working…')}</span>}
              <PersonalizationToggle />
            </div>
          </div>

          {overflow > 0 && (
            <div className="shrink-0 border-b border-primary/20 bg-primary/5 px-3 py-1 text-csm-xs text-primary">
              {t('i18n_manager.overflow', 'Showing {{cap}} of {{total}} matches — refine your search to narrow the list.', { cap: RENDER_CAP, total: filtered.length })}
            </div>
          )}

          {/* Grid */}
          <div className="min-h-0 flex-1 overflow-auto">
        <PersonalizedGrid componentKey="grid" columns={cols}>
          {(visibleCols, widths) => (
            <table className={GRID_TABLE_CLASS}>
              <thead className="sticky top-0 z-10 bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="w-7 border-b border-csm-border px-2 py-1" title={t('i18n_manager.related.header', 'Related records')} />
                  {visibleCols.map(col => (
                    <th key={col.key} className="border-b border-csm-border px-2 py-1 text-left"
                        style={widths[col.key] ? { width: widths[col.key] } : undefined}>{col.key === 'label' ? t('i18n_manager.col.label', col.header) : col.header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleRows.map(r => (
                  <Fragment key={r.id}>
                  <tr className="hover:bg-csm-surface-alt">
                    <td className="border-b border-csm-border-soft px-1 py-1 text-center align-middle" onClick={e => e.stopPropagation()}>
                      <RelatedExpandButton open={detail.isOpen(r.id)} hasChildren={hasRelated(r.id)} onToggle={() => detail.toggle(r.id)} expandLabel={t('i18n_manager.related.expand', 'Show related records')} collapseLabel={t('i18n_manager.related.collapse', 'Hide related')} tooltip={t('i18n_manager.related.tooltip', 'Show related records')} />
                    </td>
                    {visibleCols.map(col => (
                      <td key={col.key} className="border-b border-csm-border-soft px-2 py-1 align-top"
                          style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                        {col.key === 'label'
                          ? <span className="block break-all font-mono text-csm-2xs text-csm-ink-soft" title={r.label}>{r.label}</span>
                          : (
                            <input
                              type="text"
                              value={cellValue(r, col.key)}
                              onChange={e => setEdits(prev => ({ ...prev, [`${r.id} ${col.key}`]: e.target.value }))}
                              onBlur={() => onCellBlur(r, col.key)}
                              className="h-7 w-full rounded border border-transparent bg-transparent px-1 text-csm-sm hover:border-csm-border focus:border-csm-watermark focus:bg-white focus:outline-none"
                            />
                          )}
                      </td>
                    ))}
                  </tr>
                  {detail.isOpen(r.id) && (
                    <tr>
                      <td colSpan={Math.max(1, visibleCols.length) + 1} className="border-b border-csm-border bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}>
                        <RelatedChildrenPanel groups={buildRelatedGroups(r.id)} session={session} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
                {filtered.length === 0 && !busy && (
                  <tr>
                    <td colSpan={Math.max(1, visibleCols.length) + 1} className="py-12 text-center">
                      <EmptyState variant={anyFilterActive ? 'no-match' : 'no-data'} title={t('i18n_manager.no_match', 'No strings match “{{q}}”.', { q: filter })} />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </PersonalizedGrid>
          </div>
        </ContentCard>
      </div>

      {confirmAction && (
        <ConfirmModal
          danger={confirmAction === 'all'}
          title={confirmAction === 'all'
            ? t('i18n_manager.confirm_retranslate_title', 'Re-translate all?')
            : t('i18n_manager.confirm_missing_title', 'Translate missing?')}
          message={confirmAction === 'all'
            ? t('i18n_manager.confirm_retranslate_msg', 'This OVERWRITES all {{count}} {{target}} translation(s) shown, re-translating each from {{source}}. Existing {{target}} text is replaced and this cannot be undone.', { count: translatable.length, target: localeLabel(aiLocale), source: localeLabel(replaceLocale) })
            : t('i18n_manager.confirm_missing_msg', 'Translate the {{count}} string(s) with no {{target}} translation yet, from {{source}}? Existing translations are left untouched.', { count: missing.length, target: localeLabel(aiLocale), source: localeLabel(replaceLocale) })}
          confirmLabel={confirmAction === 'all'
            ? t('i18n_manager.confirm_retranslate_ok', 'Overwrite & re-translate')
            : t('i18n_manager.confirm_missing_ok', 'Translate')}
          onConfirm={runConfirmedAction}
          onCancel={() => setConfirmAction(null)}
        />
      )}
    </div>
  );
}
